import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Layers, Shield, Brain, ChevronRight } from "lucide-react";
import { EBOOK, AUTHOR } from "@/data/ebookData";

const CHAPTER_ICONS = [BookOpen, Brain, Layers, Shield, Layers, Brain];

export default function LandingPage() {
  useEffect(() => {
    document.title = "What AI Is NOT | Shadab Rashid | The AI Mastery Series";
  }, []);
  return (
    <div data-testid="landing-page">
      {/* Hero Section */}
      <section className="min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 lg:py-24 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">
                  {EBOOK.series}
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-none mt-4 font-cabinet text-[#0A0A0A]">
                  What AI Is{" "}
                  <span className="text-[#FF4F00]">NOT</span>
                </h1>
                <p className="text-base md:text-lg text-[#666666] mt-6 max-w-lg leading-relaxed font-plex">
                  {EBOOK.subtitle}. Twenty chapters of honest, no-BS understanding that most books are too afraid to write.
                </p>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="flex flex-wrap gap-6 md:gap-10 mt-8 py-6 border-t border-b border-[#E5E5E5]"
              >
                {[
                  { value: EBOOK.stats.chapters, label: "Chapters" },
                  { value: EBOOK.stats.pages, label: "Pages" },
                  { value: EBOOK.stats.reviews, label: "Expert Reviews" },
                  { value: EBOOK.stats.hype, label: "Hype" },
                ].map((stat) => (
                  <div key={stat.label} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, "-")}`}>
                    <span className="font-cabinet text-2xl md:text-3xl font-bold text-[#0A0A0A]">{stat.value}</span>
                    <span className="block text-xs text-[#666666] font-mono-plex mt-1">{stat.label}</span>
                  </div>
                ))}
              </motion.div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="flex flex-wrap gap-4 mt-8"
              >
                <Link to="/read/what-ai-is-not" data-testid="hero-start-reading">
                  <button className="bg-[#FF4F00] text-white px-8 py-3.5 font-medium hover:bg-[#E64700] transition-colors flex items-center gap-2 text-sm">
                    Start Reading <ArrowRight size={16} />
                  </button>
                </Link>
                <Link to="/library" data-testid="hero-view-library">
                  <button className="bg-transparent border border-[#0A0A0A] text-[#0A0A0A] px-8 py-3.5 font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors text-sm">
                    View Library
                  </button>
                </Link>
              </motion.div>
            </div>

            {/* Book Cover */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, rotateY: -15 }}
              animate={{ opacity: 1, scale: 1, rotateY: -8 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end"
            >
              <div className="book-cover-wrapper">
                <div className="book-cover relative w-[240px] md:w-[300px] lg:w-[340px] aspect-[3/4] overflow-hidden border border-[#E5E5E5] bg-[#0A0A0A] flex flex-col justify-between p-6 md:p-8">
                  <div>
                    <p className="font-mono-plex text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-[#FF4F00]">
                      {EBOOK.series}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-cabinet font-bold text-3xl md:text-4xl lg:text-5xl text-white leading-none tracking-tighter">
                      What AI<br />Is <span className="text-[#FF4F00]">NOT</span>
                    </h3>
                    <div className="w-10 h-[2px] bg-[#FF4F00] mt-4 mb-4" />
                    <p className="font-mono-plex text-[8px] md:text-[9px] tracking-[0.12em] uppercase text-white/40 mb-1">
                      Thought by
                    </p>
                    <p className="font-cabinet font-bold text-lg md:text-xl lg:text-2xl text-white tracking-tight">
                      Shadab Rashid
                    </p>
                    <div className="w-6 h-[1px] bg-white/20 mt-3 mb-3" />
                    <p className="font-mono-plex text-[7px] md:text-[8px] tracking-[0.12em] uppercase text-white/40 mb-0.5">
                      Compiled &amp; Curated by
                    </p>
                    <p className="font-cabinet font-medium text-sm md:text-base text-white/80 tracking-tight">
                      Shifa Shaikh
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Chapter Highlights */}
      <section className="border-t border-[#E5E5E5]" data-testid="chapter-highlights">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 lg:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">
                20 Chapters
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 font-cabinet text-[#0A0A0A]">
                Inside the Book
              </h2>
            </div>
            <Link to="/read/what-ai-is-not" className="text-sm text-[#FF4F00] font-medium flex items-center gap-1 mt-4 md:mt-0 link-underline">
              Read all chapters <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {EBOOK.chapters.slice(0, 6).map((ch, i) => {
              const Icon = CHAPTER_ICONS[i % CHAPTER_ICONS.length];
              return (
                <motion.div
                  key={ch.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: i * 0.08 }}
                >
                  <Link
                    to={`/read/what-ai-is-not/${ch.id}`}
                    className="block card-hover bg-white p-6 h-full group"
                    data-testid={`chapter-preview-${ch.id}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono-plex text-xs tracking-[0.15em] text-[#FF4F00] font-bold">
                        Ch {ch.number}
                      </span>
                      <Icon size={16} className="text-[#E5E5E5] group-hover:text-[#FF4F00] transition-colors" />
                    </div>
                    <h3 className="font-cabinet font-bold text-base text-[#0A0A0A] leading-snug">
                      {ch.title}
                    </h3>
                    <p className="text-sm text-[#666666] mt-2 leading-relaxed font-plex">
                      {ch.subtitle}
                    </p>
                    <div className="flex items-center gap-1 mt-4 text-xs text-[#FF4F00] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Read chapter <ChevronRight size={12} />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="border-t border-[#E5E5E5] bg-white" data-testid="takeaways-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">
                Key Takeaways
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 font-cabinet text-[#0A0A0A]">
                What You'll Learn
              </h2>
              <p className="text-base text-[#666666] mt-4 leading-relaxed font-plex">
                Every chapter builds your practical understanding of AI, from how it actually works to who it affects and why that matters.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="space-y-0">
                {EBOOK.keyTakeaways.map((takeaway, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="flex gap-4 py-5 border-b border-[#E5E5E5] first:border-t"
                  >
                    <span className="font-mono-plex text-xs text-[#FF4F00] font-bold mt-0.5 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-sm text-[#0A0A0A] leading-relaxed font-plex">{takeaway}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Author Teaser */}
      <section className="border-t border-[#E5E5E5]" data-testid="author-teaser">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            <div className="lg:col-span-4">
              <div className="w-full max-w-[300px] aspect-[3/4] overflow-hidden border border-[#E5E5E5] bg-[#0A0A0A] flex items-center justify-center">
                <span className="font-cabinet text-7xl font-bold text-[#FF4F00]">SR</span>
              </div>
            </div>
            <div className="lg:col-span-8">
              <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">
                The Author
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 font-cabinet text-[#0A0A0A]">
                {AUTHOR.name}
              </h2>
              <p className="text-sm text-[#FF4F00] font-mono-plex mt-2">{AUTHOR.role}</p>
              <blockquote className="mt-6 border-l-2 border-[#FF4F00] pl-6">
                <p className="text-base md:text-lg leading-relaxed text-[#0A0A0A] font-cabinet italic">
                  "{AUTHOR.bookQuote}"
                </p>
              </blockquote>
              <Link
                to="/author"
                className="inline-flex items-center gap-2 mt-8 text-sm font-medium text-[#0A0A0A] border-b border-[#0A0A0A] pb-1 hover:text-[#FF4F00] hover:border-[#FF4F00] transition-colors"
                data-testid="author-teaser-link"
              >
                About the author <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-[#E5E5E5] bg-[#0A0A0A]" data-testid="final-cta">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 lg:py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">
              Ready?
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-4 font-cabinet text-white max-w-2xl mx-auto">
              Understand AI honestly. Start reading now.
            </h2>
            <p className="text-base text-[#666666] mt-4 max-w-md mx-auto font-plex">
              Twenty chapters. Zero hype. The AI education you actually need.
            </p>
            <Link to="/read/what-ai-is-not" data-testid="final-cta-button">
              <button className="mt-8 bg-[#FF4F00] text-white px-10 py-4 font-medium hover:bg-[#E64700] transition-colors flex items-center gap-2 mx-auto text-sm">
                Start Reading, Free <ArrowRight size={16} />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
